import { history as historyRouter } from 'instantsearch.js/es/lib/routers';

const ARRAY_SEPARATOR = '~';

function serializeList(values) {
  if (!Array.isArray(values) || !values.length) {
    return '';
  }

  return values.join(ARRAY_SEPARATOR);
}

function deserializeList(value) {
  if (!value) {
    return [];
  }

  return value.split(ARRAY_SEPARATOR).filter(Boolean);
}

function parsePage(value) {
  const page = Number(value);

  if (!Number.isInteger(page) || page < 1) {
    return undefined;
  }

  return page;
}

function getPersistedQuery(routeState, location, queryParameter) {
  if (routeState.q) {
    return routeState.q;
  }

  const currentParams = new URLSearchParams(location.search);
  return currentParams.get(queryParameter) || '';
}

export function createRouting(config) {
  const initialDocumentTitle =
    typeof document !== 'undefined' ? config.documentTitle || document.title : config.documentTitle || '';
  const managedKeys = [
    config.queryParameter,
    'page',
    'sort',
    'product_type',
    'type',
    'color',
    'size',
    'available',
    'price',
  ];

  return {
    router: historyRouter({
      cleanUrlOnDispose: false,
      windowTitle() {
        return initialDocumentTitle;
      },
      createURL({ location, routeState }) {
        const params = new URLSearchParams(location.search);
        const persistedQuery = getPersistedQuery(routeState, location, config.queryParameter);

        managedKeys.forEach((key) => params.delete(key));

        if (persistedQuery) {
          params.set(config.queryParameter, persistedQuery);
        }

        if (routeState.page) {
          params.set('page', String(routeState.page));
        }

        if (routeState.sort) {
          params.set('sort', routeState.sort);
        }

        if (routeState.product_type) {
          params.set('product_type', routeState.product_type);
        }

        if (routeState.type) {
          params.set('type', routeState.type);
        }

        if (routeState.color) {
          params.set('color', routeState.color);
        }

        if (routeState.size) {
          params.set('size', routeState.size);
        }

        if (routeState.available) {
          params.set('available', routeState.available);
        }

        if (routeState.price) {
          params.set('price', routeState.price);
        }

        const queryString = params.toString();

        return `${location.pathname}${queryString ? `?${queryString}` : ''}${location.hash}`;
      },
      parseURL({ location }) {
        const params = new URLSearchParams(location.search);

        return {
          q: params.get(config.queryParameter) || '',
          page: parsePage(params.get('page')),
          sort: params.get('sort') || '',
          product_type: params.get('product_type') || '',
          type: params.get('type') || '',
          color: params.get('color') || '',
          size: params.get('size') || '',
          available: params.get('available') || '',
          price: params.get('price') || '',
        };
      },
    }),
    stateMapping: {
      stateToRoute(uiState) {
        const indexUiState = uiState[config.indexName] || {};
        const refinementList = indexUiState.refinementList || {};
        const toggle = indexUiState.toggle || {};
        const range = indexUiState.range || {};

        return {
          q: indexUiState.query || '',
          page: indexUiState.page,
          sort: indexUiState.sortBy || '',
          product_type: serializeList(refinementList[config.attributes.productType]),
          type: serializeList(refinementList[config.attributes.type]),
          color: serializeList(refinementList[config.attributes.color]),
          size: serializeList(refinementList[config.attributes.size]),
          available: toggle[config.attributes.availability] ? 'true' : '',
          price: range[config.attributes.price] || '',
        };
      },
      routeToState(routeState) {
        const refinementList = {};
        const toggle = {};
        const range = {};
        const indexUiState = {};

        if (routeState.q) {
          indexUiState.query = routeState.q;
        }

        if (routeState.page) {
          indexUiState.page = routeState.page;
        }

        if (routeState.sort) {
          indexUiState.sortBy = routeState.sort;
        }

        if (config.attributes.productType && routeState.product_type) {
          refinementList[config.attributes.productType] = deserializeList(routeState.product_type);
        }

        if (config.attributes.type && routeState.type) {
          refinementList[config.attributes.type] = deserializeList(routeState.type);
        }

        if (config.attributes.color && routeState.color) {
          refinementList[config.attributes.color] = deserializeList(routeState.color);
        }

        if (config.attributes.size && routeState.size) {
          refinementList[config.attributes.size] = deserializeList(routeState.size);
        }

        if (Object.keys(refinementList).length) {
          indexUiState.refinementList = refinementList;
        }

        if (config.attributes.availability && routeState.available === 'true') {
          toggle[config.attributes.availability] = true;
          indexUiState.toggle = toggle;
        }

        if (config.attributes.price && routeState.price) {
          range[config.attributes.price] = routeState.price;
          indexUiState.range = range;
        }

        return {
          [config.indexName]: indexUiState,
        };
      },
    },
  };
}
